import Cookie from 'services/Cookie';
import {
  getTokenLastCompanyId,
  mergeCompanyLists,
  resolveCompanyFromSubdomain,
  resolvePreferredCompanyId,
} from 'utils/activeCompany';
import { getStoreState } from 'utils/authStateRegistry';
import getSubdomain from 'utils/getSubdomain';
import { isReactNativeWebView } from 'utils/webAuthn';
import { getWebViewSession } from 'utils/webViewSession';

const OBJECT_ID = /^[a-f\d]{24}$/i;

/**
 * Store builds before the in-memory WebView session (RN ≤2.23) still set a host-only
 * `company` cookie on the apex sign URL. Tenant routes 401 without `x-company` there,
 * and that 401 deletes the app bearer. The cookie is not consulted on a company subdomain.
 */
function companyIdFromLegacyAppCookie() {
  if (!isReactNativeWebView()) {
    return null;
  }
  const id = Cookie.get('company')?._id;
  return typeof id === 'string' && OBJECT_ID.test(id) ? id : null;
}

export function getActiveAuthToken(auth) {
  if (auth?.impersonation?.token?.hash) {
    return auth.impersonation.token;
  }
  // On a freshly-loaded origin (subdomain hop / reload), Redux impersonation state has not been
  // restored yet, but the shared `impersonate` cookie carries the scoped token across origins.
  const impersonateCookie = Cookie.get('impersonate');
  if (impersonateCookie?.hash) {
    return impersonateCookie;
  }
  if (auth?.token?.hash) {
    return auth.token;
  }
  const cookieToken = Cookie.get('token');
  if (cookieToken?.hash) {
    return cookieToken;
  }
  // RN OmmnioSign WebView: app session injected by the app (utils/webViewSession), memory only.
  const webViewSession = getWebViewSession();
  if (webViewSession) {
    return { hash: webViewSession.token, isWebViewSession: true };
  }
  return cookieToken;
}

/**
 * Resolve the active company `_id` for the `x-company` header.
 *
 * The URL subdomain is the source of truth: map it to a company `_id` using the companies we
 * already know (entity cache ∪ auth user companies). This makes the tenant header correct on the
 * very first request after a cross-subdomain hop, instead of depending on a per-origin
 * sessionStorage value that has not been seeded yet (the cause of the post-switch 401 -> logout).
 *
 * Falls back to the persisted `company_id` and then the sessionStorage cache for apex/preview hosts
 * where the URL carries no company subdomain.
 */
export function getActiveCompanyIdFromState(state) {
  const subdomain = getSubdomain();
  if (subdomain) {
    const companies = mergeCompanyLists(state?.companies, state?.auth?.employee?.user?.companies);
    const match = resolveCompanyFromSubdomain(companies, subdomain);
    if (match?._id) {
      return match._id;
    }
    // Subdomain is canonical; never send a stale apex/switch x-company while membership is
    // unresolved (causes 401 → /logout ↔ /auth/login loop after a company hop).
    return null;
  }
  // Only when no real session won getActiveAuthToken (a real session always wins).
  const webViewSession = getWebViewSession();
  if (webViewSession && getActiveAuthToken(state?.auth)?.isWebViewSession) {
    return webViewSession.companyId;
  }
  const auth = state?.auth;
  const persistedTokenLastCompany = isImpersonatingState(state)
    ? null
    : getTokenLastCompanyId() || auth?.token_last_company_id;
  return (
    resolvePreferredCompanyId(persistedTokenLastCompany, auth?.company_id) ||
    companyIdFromLegacyAppCookie()
  );
}

export function getActiveAuthHeaders(state) {
  const resolved = state?.auth ? state : getStoreState();
  const auth = resolved?.auth;
  const companyId = getActiveCompanyIdFromState(resolved);
  const token = getActiveAuthToken(auth);

  const headers = {};
  if (token?.hash) {
    headers.authorization = `Bearer ${token.hash}`;
  }
  if (companyId) {
    headers['x-company'] = companyId;
  }
  return headers;
}

export function isImpersonatingState(state) {
  const auth = state?.auth ?? getStoreState()?.auth;
  if (auth?.impersonation?.token?.hash) {
    return true;
  }
  // Fresh origin before Redux restore: the shared cookie still marks an active impersonation,
  // so a 401 drops back to superadmin instead of logging the whole session out.
  return !!Cookie.get('impersonate')?.hash;
}
